/**
 * Zwischenspeicher für den 2FA-Status der aktuellen Anmeldesitzung.
 *
 * Warum: Der interne Bereich prüfte den 2FA-Status bei JEDEM Seitenwechsel
 * serverseitig. Das kostete Zeit und machte jede kurze Störung (Token-
 * Erneuerung, Netzaussetzer) als scheinbar grundlose Weiterleitung auf die
 * 2FA-Seite sichtbar. Der Status wird deshalb an die Sitzungskennung des
 * Zugriffstokens gebunden gemerkt. Neue Anmeldung = neue Sitzungskennung =
 * neue Prüfung. Der Server bleibt die einzige Wahrheit; hier wird nur die
 * Häufigkeit der Abfrage begrenzt.
 */

const PREFIX = "creategy:mfa-verified:";

/** Gültigkeitsdauer des gemerkten Zustands in Millisekunden (10 Minuten). */
export const MFA_CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Liest die Sitzungskennung (`session_id`) aus einem Zugriffstoken.
 * Es wird ausschließlich als Speicherschlüssel benutzt – keine Prüfung von
 * Signatur oder Rechten (das macht der Server).
 */
export function readSessionId(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;
  const parts = accessToken.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const json = JSON.parse(
      typeof atob === "function"
        ? decodeURIComponent(
            atob(padded)
              .split("")
              .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
              .join(""),
          )
        : Buffer.from(padded, "base64").toString("utf8"),
    ) as Record<string, unknown>;
    return typeof json.session_id === "string" ? json.session_id : null;
  } catch {
    return null;
  }
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

function store(): Store | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** true, wenn die Sitzung innerhalb der Gültigkeitsdauer als bestätigt gilt. */
export function isMfaVerifiedCached(
  sessionId: string | null,
  now: number = Date.now(),
  storage: Store | null = store(),
): boolean {
  if (!sessionId || !storage) return false;
  try {
    const raw = storage.getItem(PREFIX + sessionId);
    if (!raw) return false;
    const until = Number.parseInt(raw, 10);
    if (!Number.isFinite(until)) return false;
    if (until <= now) {
      storage.removeItem(PREFIX + sessionId);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Merkt die Sitzung als 2FA-bestätigt. */
export function rememberMfaVerified(
  sessionId: string | null,
  now: number = Date.now(),
  storage: Store | null = store(),
): void {
  if (!sessionId || !storage) return;
  try {
    storage.setItem(PREFIX + sessionId, String(now + MFA_CACHE_TTL_MS));
  } catch {
    /* Speicher kann blockiert sein – dann wird eben jedes Mal geprüft. */
  }
}

/** Löscht alle gemerkten Zustände (Anmeldung, Abmeldung, Kontowechsel). */
export function clearMfaVerifiedCache(storage: Store | null = store()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* nichts zu tun */
  }
}
